// API service for managing practice sessions in DynamoDB
import { DynamoDBClient } from '@aws-sdk/client-dynamodb'
import { 
  DynamoDBDocumentClient, 
  PutCommand, 
  GetCommand, 
  QueryCommand, 
  DeleteCommand,
  UpdateCommand 
} from '@aws-sdk/lib-dynamodb'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import { nanoid } from 'nanoid'

// Initialize DynamoDB client with credentials from Amplify
let docClient = null

const getDocClient = async () => {
  if (docClient) return docClient
  
  // Check if Identity Pool is configured
  const identityPoolId = import.meta.env.VITE_AWS_IDENTITY_POOL_ID
  if (!identityPoolId) {
    throw new Error('Identity Pool ID is not configured. Please set VITE_AWS_IDENTITY_POOL_ID in your .env file.')
  }
  
  try {
    // First, verify user is authenticated
    try {
      await getCurrentUser()
    } catch (authError) {
      throw new Error('User is not authenticated. Please log in first.')
    }
    
    // Fetch auth session - force refresh to ensure we get Identity Pool credentials
    const session = await fetchAuthSession({ forceRefresh: true })
    const credentials = session.credentials
    
    if (!credentials || !credentials.accessKeyId) {
      throw new Error('No AWS credentials available. Please ensure:\n1. Identity Pool is configured correctly\n2. You are authenticated\n3. Identity Pool is linked to your User Pool\n4. Try signing out and signing back in')
    }
    
    const client = new DynamoDBClient({ 
      region: import.meta.env.VITE_AWS_REGION || 'us-east-2',
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
      }
    })
    
    docClient = DynamoDBDocumentClient.from(client, {
      marshallOptions: {
        removeUndefinedValues: true
      }
    })
    return docClient
  } catch (error) {
    // Re-throw with more context if it's our custom error
    if (error.message.includes('Identity Pool') || error.message.includes('credentials')) {
      throw error
    }
    
    throw new Error(`Failed to get AWS credentials: ${error.message}. Please check your Identity Pool configuration.`)
  }
}

const TABLE_NAME = import.meta.env.VITE_DYNAMODB_PRACTICE_SESSIONS_TABLE_NAME || 'practice-sessions'

// Get current user ID (Identity Pool identity ID - full format)
// This matches the IAM policy condition: ${cognito-identity.amazonaws.com:sub}
const getUserId = async () => {
  try {
    // First verify user is authenticated
    await getCurrentUser()
    
    // Get Identity Pool identity ID from session
    const session = await fetchAuthSession({ forceRefresh: true })
    
    if (!session.identityId) {
      throw new Error('Identity Pool identity ID not available. Please ensure:\n1. Identity Pool is properly linked to your User Pool in AWS Console\n2. You have signed out and signed back in after configuring Identity Pool\n3. Identity Pool ID is correctly set in .env file')
    }
    
    return session.identityId
  } catch (error) {
    if (error.message.includes('Identity Pool') || error.message.includes('identity ID')) {
      throw error
    }
    throw new Error('User not authenticated')
  }
}

// Save a practice session (create or update)
export const savePracticeSession = async (sessionData, isPublic = false) => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  // Use nanoid for shorter session IDs (8 characters, URL-safe)
  // Keep existing sessionId if provided (for updates), otherwise generate new one
  const sessionId = sessionData.id || nanoid(8)
  const now = new Date().toISOString()
  
  const tags = sessionData.tags || []
  const item = {
    userId,
    sessionId,
    name: sessionData.name || 'Untitled Practice Session',
    description: sessionData.description || '',
    sportType: sessionData.sportType || 'soccer',
    tags: tags, // Array of tag strings
    // Store first tag for GSI (sparse index - only items with tags will have this)
    ...(tags.length > 0 && { tag: tags[0] }),
    tiles: sessionData.tiles || [],
    notes: sessionData.notes || '',
    isPublic: isPublic ? 'true' : 'false', // Store as string for GSI
    updatedAt: now, // Store as string for GSI sorting
    createdAt: sessionData.createdAt || now
  }
  
  try {
    await client.send(new PutCommand({
      TableName: TABLE_NAME,
      Item: item
    }))
    
    return {
      ...item,
      id: sessionId,
      isPublic: isPublic
    }
  } catch (error) {
    console.error('Error saving practice session:', error)
    throw new Error('Failed to save practice session: ' + error.message)
  }
}

// Load a practice session by ID
// isUserOwnSession:
//   - true: Load as the current user's own session (from "My Sessions" tab)
//   - false: Load as a public session (from "Public Sessions" tab)
//   - undefined/null: Try user's own first, then fallback to public (for URL-based loading)
export const loadPracticeSession = async (sessionId, isUserOwnSession = undefined) => {
  if (isUserOwnSession === true) {
    // Explicitly load as the current user's own session
    return await loadUserPracticeSession(sessionId)
  } else if (isUserOwnSession === false) {
    // Explicitly load as a public session
    return await loadPublicPracticeSession(sessionId)
  } else {
    // Unknown context (e.g., URL loading) - try user's own first, then public
    try {
      return await loadUserPracticeSession(sessionId)
    } catch (error) {
      // If user's own session fails, try public session
      return await loadPublicPracticeSession(sessionId)
    }
  }
}

// Load a practice session that belongs to the current user
export const loadUserPracticeSession = async (sessionId) => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  try {
    const result = await client.send(new GetCommand({
      TableName: TABLE_NAME,
      Key: {
        userId,
        sessionId
      }
    }))
    
    if (!result.Item) {
      throw new Error('Practice session not found')
    }
    
    return formatSessionFromDynamo(result.Item)
  } catch (error) {
    console.error('Error loading user\'s practice session:', error)
    throw new Error('Failed to load practice session: ' + error.message)
  }
}

// Load a public practice session by ID
export const loadPublicPracticeSession = async (sessionId) => {
  const client = await getDocClient()
  
  try {
    // Query public sessions index to find the session
    // Note: FilterExpression is applied AFTER Limit, so we don't use Limit to ensure
    // we can find the session even if it's not in the first N items returned
    const result = await client.send(new QueryCommand({
      TableName: TABLE_NAME,
      IndexName: 'public-sessions-index',
      KeyConditionExpression: 'isPublic = :public',
      FilterExpression: 'sessionId = :sessionId',
      ExpressionAttributeValues: {
        ':public': 'true',
        ':sessionId': sessionId
      }
    }))
    
    if (result.Items && result.Items.length > 0) {
      return formatSessionFromDynamo(result.Items[0])
    }
    
    throw new Error('Practice session not found')
  } catch (error) {
    console.error('Error loading public practice session:', error)
    throw new Error('Failed to load practice session: ' + error.message)
  }
}

// Get all practice sessions for the current user
export const getUserPracticeSessions = async () => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  try {
    const result = await client.send(new QueryCommand({
      TableName: TABLE_NAME,
      KeyConditionExpression: 'userId = :userId',
      ExpressionAttributeValues: {
        ':userId': userId
      },
      ScanIndexForward: false // Sort by sessionId descending (newest first)
    }))
    
    return (result.Items || []).map(formatSessionFromDynamo)
  } catch (error) {
    console.error('Error getting user practice sessions:', error)
    throw new Error('Failed to get practice sessions: ' + error.message)
  }
}

// Get all public practice sessions
export const getPublicPracticeSessions = async (limit = 50) => {
  const client = await getDocClient()
  
  try {
    const result = await client.send(new QueryCommand({
      TableName: TABLE_NAME,
      IndexName: 'public-sessions-index',
      KeyConditionExpression: 'isPublic = :public',
      ExpressionAttributeValues: {
        ':public': 'true'
      },
      ScanIndexForward: false, // Sort by updatedAt descending (newest first)
      Limit: limit
    }))
    
    return (result.Items || []).map(formatSessionFromDynamo)
  } catch (error) {
    console.error('Error getting public practice sessions:', error)
    throw new Error('Failed to get public practice sessions: ' + error.message)
  }
}

// Delete a practice session
export const deletePracticeSession = async (sessionId) => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  try {
    await client.send(new DeleteCommand({
      TableName: TABLE_NAME,
      Key: {
        userId,
        sessionId
      }
    }))
    
    return true
  } catch (error) {
    console.error('Error deleting practice session:', error)
    throw new Error('Failed to delete practice session: ' + error.message)
  }
}

// Format practice session data from DynamoDB format to app format
const formatSessionFromDynamo = (item) => {
  return {
    id: item.sessionId,
    name: item.name,
    description: item.description || '',
    sportType: item.sportType || 'soccer',
    tags: item.tags || [], // Array of tag strings
    tiles: item.tiles || [],
    notes: item.notes || '',
    isPublic: item.isPublic === 'true',
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    userId: item.userId // Keep for reference
  }
}

